import { Platform, Text } from "react-native";
import { Moment } from "moment";
import { ActionsContainer, Button, ScrollableContainer, ScreenContainer, PopOver } from "../../components";
import { Feather } from "@expo/vector-icons";
import { View } from "react-native";
import { Employment, Person, RootStackScreenProps } from "../types";
import { gql, useQuery } from "@apollo/client";
import { ActivityIndicator } from "react-native-paper";
import { ADDRESS_FRAGMENT } from "../fragments";
import Address from "../../components/Address";
import { useEffect, useState } from "react";
import { colors } from "@mui/material";
import DatePickerInput from "../../components/DatePickerInput";
import { isEmpty } from "lodash";
import { EmploymentsToRemove } from "../AddEmploymentScreen/types";
import { readableDate } from "../../utils/dateUtils";
import UnsavedChangesModal from "../../components/UnsavedChangesModal";
import FutureDateEmploymentPopOver, { getFutureDate } from "../../components/FutureDateEmploymentPopOver";

export const EMPLOYMENTS_QUERY = gql`
  query currentEmployments {
    currentPerson {
      id
      employments {
        id
        startedOn
        title
        isPrimary
        facility {
          id
          name
          addresses {
            ...AddressFragment
          }
          section {
            id
            name
          }
        }
      }
    }
  }
  ${ADDRESS_FRAGMENT}
`;

interface EmploymentsQuery {
  currentPerson: Person;
}

type EmploymentFormState = { keep: null | true | false; dateEnded?: string };

const CurrentEmploymentReviewContent = ({
  onContinue,
  employments = [],
  setIsDirty,
}: {
  onContinue(employmentsToRemove: EmploymentsToRemove[]): void;
  employments: Employment[];
  setIsDirty: (isDirty: boolean) => void;
}) => {
  const [employmentFormStates, setEmploymentFormStates] = useState<Record<string, EmploymentFormState>>(
    employments.reduce((acc, employment) => ({ ...acc, [employment.id]: { keep: null } }), {})
  );
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [shouldValidate, setShouldValidate] = useState<boolean>(false);
  const [futureDateValue, setFutureDateValue] = useState<string | null>(null);
  const [showMultipleEmploymentPopOver, setShowMultipleEmploymentPopOver] = useState<boolean>(false);
  const [employmentsToRemove, setEmploymentsToRemove] = useState<EmploymentsToRemove[]>([]);

  const validate = () => {
    const missingSelections = Object.keys(employmentFormStates).reduce(
      (acc, key) => {
        const { keep, dateEnded } = employmentFormStates[key];
        let errorMessage;

        if (keep === null) errorMessage = "Employment status is required";
        if (keep === false && !dateEnded) errorMessage = "Employment end date is required";

        if (errorMessage) {
          acc[key] = errorMessage;
        }
        return acc;
      },
      {} as { [key: string]: string }
    );

    setErrors(missingSelections);
    return missingSelections;
  };

  const continueToAddEmployment = () => {
    setShouldValidate(true);

    const missingSelections = validate();

    const employmentsToRemove = Object.keys(employmentFormStates).reduce((acc, key) => {
      const { dateEnded } = employmentFormStates[key];
      if (dateEnded) {
        acc.push({
          id: key,
          dateEnded,
        });
      }
      return acc;
    }, [] as EmploymentsToRemove[]);
    const hasOneEmploymentKeep = Object.values(employmentFormStates).some((e) => e.keep);

    if (isEmpty(missingSelections)) {
      if (hasOneEmploymentKeep) {
        setEmploymentsToRemove(employmentsToRemove);
        setShowMultipleEmploymentPopOver(true);
      } else {
        onContinue(employmentsToRemove);
      }
    }
  };

  useEffect(() => {
    if (shouldValidate) {
      validate();
    }
  }, [employmentFormStates]);

  const validateStartDate = (startedOn: string) => (date: Moment) => {
    if (date.isBefore(startedOn)) return `End date must be after the start date of ${readableDate(startedOn)}.`;
  };

  return (
    <View className="flex-1 flex-col">
      <FutureDateEmploymentPopOver {...{ futureDateValue, setFutureDateValue }} />
      <PopOver
        onDismiss={() => setShowMultipleEmploymentPopOver(false)}
        visible={showMultipleEmploymentPopOver}
        testID="multiple-employment-pop-over"
      >
        <Text className="text-title-5 font-700 text-ink-darkest">Notice: Multiple Employment Records</Text>

        <View className="my-4">
          <Text className="text-regular-normal text-ink-dark font-400">
            According to PGA policies, you should maintain only one active employment record unless you are{" "}
            <Text className="text-regular-normal text-ink-dark font-700">
              concurrently holding more than one full-time position.
            </Text>
          </Text>

          <Text className="text-regular-normal text-ink-dark font-400 mt-4">
            Are you working full-time at the job you wish to keep, alongside the new job you're adding? If not, go back and
            remove the job to continue.
          </Text>
        </View>

        <Button mode="contained" onPress={() => setShowMultipleEmploymentPopOver(false)}>
          Go Back and Remove
        </Button>
        <Button
          onPress={() => {
            setShowMultipleEmploymentPopOver(false);
            onContinue(employmentsToRemove);
          }}
          className="mt-2"
        >
          Keep Job and Continue
        </Button>
      </PopOver>
      <ScrollableContainer className="flex-1">
        <View className="p-6 justify-between">
          <Text className="text-regular-tight font-700 text-ink-darkest">
            Review your current employment records before proceeding.
          </Text>

          <Text className="text-regular-tight font-400 text-ink mt-4">
            You have previous employment records on file. Please review and verify employment is current before adding new
            employment.
          </Text>
        </View>
        <View className="p-6 pt-0 flex-col justify-between">
          {employments.map((employment) => {
            const isBeingKept = employmentFormStates[employment.id].keep;
            const isBeingRemoved = employmentFormStates[employment.id].keep === false;
            return (
              <View key={employment.id} className="mt-4 rounded-lg border border-sky bg-sky-white flex p-6">
                <Text className="text-regular-tight font-700 text-ink-darkest mb-4">{employment.title ?? "None"}</Text>
                <Text className="text-regular-normal text-ink-darkest">{employment.facility.name}</Text>
                {employment.facility.addresses.length ? <Address address={employment.facility.addresses[0]} /> : null}
                {errors[employment.id] && !errors[employment.id].includes("end date") && (
                  <Text className="text-red-dark text-regular-normal mt-6">{errors[employment.id]}</Text>
                )}
                <View className="flex flex-row mt-6 justify-center">
                  <Button
                    testID="keepButton"
                    size="small"
                    mode={isBeingKept ? "selected" : "outlined"}
                    {...(isBeingKept ? { icon: () => <Feather name="check" size={16} color={colors.common.white} /> } : {})}
                    className="w-1/2 mb-2 mr-1"
                    onPress={() => {
                      setIsDirty(true);
                      setEmploymentFormStates({
                        ...employmentFormStates,
                        [employment.id]: { keep: true },
                      });
                    }}
                  >
                    Keep
                  </Button>
                  <Button
                    testID="removeButton"
                    mode={isBeingRemoved ? "rejected" : "outlined"}
                    size="small"
                    {...(isBeingRemoved ? { icon: () => <Feather name="x" size={16} color={colors.common.white} /> } : {})}
                    className="w-1/2 ml-1 mb-2"
                    onPress={() => {
                      setIsDirty(true);
                      setEmploymentFormStates({
                        ...employmentFormStates,
                        [employment.id]: { keep: false, dateEnded: "" },
                      });
                    }}
                  >
                    Remove
                  </Button>
                </View>
                {isBeingRemoved ? (
                  <View className="mt-4">
                    <DatePickerInput
                      testID="DatePickerInput"
                      onChange={(dateEnded) => {
                        setEmploymentFormStates({
                          ...employmentFormStates,
                          [employment.id]: { keep: false, dateEnded },
                        });
                        setFutureDateValue(getFutureDate(dateEnded));
                      }}
                      title="Employment End Date"
                      value={employmentFormStates[employment.id].dateEnded}
                      description="Employment End Date"
                      placeholder="Employment End Date"
                      validate={validateStartDate(employment.startedOn)}
                      onBlur={() => {}}
                      error={errors[employment.id] ? { message: errors[employment.id] } : undefined}
                    />
                  </View>
                ) : null}
              </View>
            );
          })}
        </View>
      </ScrollableContainer>
      <ActionsContainer>
        <Button
          isValid={Object.values(employmentFormStates).every((e) => e.keep !== null)}
          mode="contained"
          onPress={() => continueToAddEmployment()}
        >
          Continue
        </Button>
      </ActionsContainer>
    </View>
  );
};

const CurrentEmploymentReviewScreen = ({ navigation }: RootStackScreenProps<"CurrentEmploymentReview">) => {
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [discardChanges, setDiscardChanges] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const onContinue = (employmentsToRemove: EmploymentsToRemove[]) =>
    navigation.navigate("AddEmployment", { employmentsToRemove });

  function goBackWeb() {
    if (isDirty) {
      setShowUnsavedChangesModal(true);
      return;
    }
    if (window && window.history) {
      return window.history.back();
    }
    navigation.goBack();
  }

  useEffect(() => {
    if (discardChanges) return navigation.goBack();
    if (Platform.OS === "web") return;
    const unsubscribe = navigation.addListener("beforeRemove", (event) => {
      if (isDirty) {
        event.preventDefault();
        setShowUnsavedChangesModal(true);
      }
    });

    return unsubscribe;
  }, [navigation, discardChanges, isDirty]);

  const { data, loading: queryLoading } = useQuery<EmploymentsQuery>(EMPLOYMENTS_QUERY, {
    fetchPolicy: "cache-first",
  });

  if (queryLoading) {
    return <ActivityIndicator className="flex-1 items-center justify-center bg-white" />;
  }

  const employments = data?.currentPerson.employments || [];
  return (
    <ScreenContainer
      testID="currentEmploymentReviewScreen"
      navigationTitle="Add Employment"
      goBack={() => setShowUnsavedChangesModal(true)}
      overrideBack={goBackWeb}
    >
      <UnsavedChangesModal
        mutationLoading={false}
        showUnsavedChangesModal={showUnsavedChangesModal}
        setShowUnsavedChangesModal={setShowUnsavedChangesModal}
        discardAndExit={() => setDiscardChanges(true)}
        handleSubmit={() => setShowUnsavedChangesModal(false)}
        title="Exit and cancel employment change?"
        message="If you exit now, your changes will not be saved."
        buttonLabel="Continue"
        cancelButtonLabel="Exit & Cancel"
        size="md"
      />
      <CurrentEmploymentReviewContent employments={employments} onContinue={onContinue} setIsDirty={setIsDirty} />
    </ScreenContainer>
  );
};

export default CurrentEmploymentReviewScreen;
